import NavResponsiveModal from '@/components/Navigation/NavResponsiveModal';
import { SUBMIT_ROUTE } from '@/constants/apiRoutes';
import { post } from '@/utilities/onwardClient';
import { FormControlLabel, Grid, Radio, RadioGroup } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React, { useContext, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { SUBMIT_RECONCILE } from '../graphql/queries';
import {
    Body1,
    Body2,
    GridItemRow,
    ModalActions,
    ModalContent,
    ModalTitle,
    PrimaryButton,
    SecondaryButton,
} from '../blocks';
import { MODALS } from '../constants';
import { PlanningContext } from '../context';
import { useAfterHours } from '../hooks';
import { captureException } from '@sentry/react';
import { css } from '@emotion/react';
import { checkRoutesCeilings } from '../util/resourceCeilings';
import CeilingWarnings from './CeilingWarnings';

const RouteSubmitOnwardModal = () => {
    const {
        modalOpen,
        setModalOpen,
        actionableRoute,
        setActionableRoute,
        setError,
        setNotification,
        callbacks: { refetch },
    } = useContext(PlanningContext);
    const client = useApolloClient();
    const [sendSMS, setSendSMS] = useState('shipperPreference');
    const [loading, setLoading] = useState(false);

    const afterHours = useAfterHours(actionableRoute);

    const { warned } = useMemo(() => checkRoutesCeilings(actionableRoute ? [actionableRoute] : []), [actionableRoute]);

    const handleModalClose = () => {
        setLoading(false);
        setModalOpen(null);
        setActionableRoute(null);
    };

    const handleSubmitOnward = async () => {
        if (actionableRoute?.need_to_optimize) {
            return setNotification({
                severity: 'warning',
                message: 'Route may have been changed recently, please Map or Optimize route and try again',
            });
        }

        try {
            setLoading(true);

            const submitResponse = await post(SUBMIT_ROUTE, {
                routeId: actionableRoute.route_id,
                sendSMS: sendSMS === 'yes',
                useShipperPreference: sendSMS === 'shipperPreference',
                sendingTo: 'marketplace',
            });

            if (submitResponse.status === 200) {
                setNotification({
                    severity: 'success',
                    message: `Successfully submitted Route ${actionableRoute.route_number} to Onward!`,
                });
                client
                    .query({
                        query: SUBMIT_RECONCILE,
                        variables: {
                            route_ids: [actionableRoute.route_id],
                            order_ids: (actionableRoute.orders || []).map((m) => m.order_id),
                        },
                        fetchPolicy: 'network-only',
                    })
                    .catch(() => refetch());
                handleModalClose();
            } else {
                captureException(
                    new Error(
                        `Error submitting route. ${
                            submitResponse?.data?.error || submitResponse?.data?.errorMessage
                        } Check function logs for more information.`
                    )
                );
                setNotification({
                    severity: 'error',
                    message:
                        submitResponse?.data?.errorMessage ||
                        'Error submitting route, please verify all stops have timeframes and try again',
                });
                setLoading(false);
            }
        } catch (error) {
            setError(error, 'Error submitting route, please try again');
            setLoading(false);
        }
    };

    if (!actionableRoute) return null;

    return (
        <NavResponsiveModal open={modalOpen === MODALS.ROUTE_MARKETPLACE} onClose={handleModalClose}>
            <ModalTitle>{`Send Route ${actionableRoute.route_number} to Onward?`}</ModalTitle>
            <ModalContent>
                <CeilingWarnings warned={warned} showRouteNames={false} />
                {afterHours && sendSMS === 'yes' && (
                    <GridItemRow>
                        <Alert severity="error">
                            {`You are about to send SMS messages to customers outside of regular business hours (8 AM - 5 PM)`}
                        </Alert>
                    </GridItemRow>
                )}
                <GridItemRow>
                    <Alert severity="warning">
                        Verify all service times are correct. Customers only receive reschedule texts if timeframes
                        change by more than 30 minutes.
                    </Alert>
                </GridItemRow>
                <GridItemRow>
                    <Body2>How should Onward handle delivery timeframes?</Body2>
                </GridItemRow>
                <RadioGroup value={sendSMS} onChange={(e) => setSendSMS(e.target.value)}>
                    <GridItemRow
                        css={css`
                            margin-top: 10px !important;
                        `}
                    >
                        <FormControlLabel control={<Radio color="primary" />} value={'shipperPreference'} />
                        <Grid container>
                            <Body1>Use each shipper's preferred confirmation method</Body1>
                            <Body2>
                                Each stop is confirmed according to its shipper's account setting. Orders whose shipper
                                hasn't set a preference will default to text & email.
                            </Body2>
                        </Grid>
                    </GridItemRow>
                    <GridItemRow
                        css={css`
                            margin-bottom: 10px !important;
                        `}
                    >
                        <FormControlLabel control={<Radio color="primary" />} value={'yes'} />
                        <Grid container>
                            <Body1>
                                Send route to Onward and use Onward's system to contact customers to confirm timeframes.
                            </Body1>
                            <Body2>
                                Onward will use automated text messages to contact customers and receive confirmations.
                            </Body2>
                        </Grid>
                    </GridItemRow>
                    <GridItemRow>
                        <FormControlLabel control={<Radio color="primary" />} value={'no'} />
                        <Grid container>
                            <Body1>
                                Send route to Onward but do NOT use Onward's system to contact customers to confirm
                                timeframes.
                            </Body1>
                            <Body2>
                                Onward will NOT use automated text messages to relay timeframes and receive
                                confirmations.
                            </Body2>
                        </Grid>
                    </GridItemRow>
                </RadioGroup>
            </ModalContent>
            <ModalActions>
                <SecondaryButton disabled={loading} onClick={handleModalClose}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton disabled={loading} onClick={handleSubmitOnward}>
                    Submit to Onward
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default RouteSubmitOnwardModal;
